import {
  Content,
  Portal,
  Root,
  Trigger,
  DropdownMenuProps,
} from '@radix-ui/react-dropdown-menu';
import cl from 'clsx';
import { NavLink, NavLinkProps } from 'react-router';

import ChannelIconSvg from '@/shared/assets/icons/channelIcon.svg?react';
import SiteIconSvg from '@/shared/assets/icons/siteIcon.svg?react';
import CheckIcon from '@synapex/frontend-shared/icons/check-15x12.svg?react';
import ChevronDown from '@synapex/frontend-shared/icons/chevron-down-24.svg?react';
import CirclePlaceholder from '@synapex/frontend-shared/icons/circle-50.svg?react';
import PlusIcon from '@synapex/frontend-shared/icons/plus-13.svg?react';
import { siteMethods, workspaceMethods } from '@synapex/shared2/entities';

import classes from './WorkspaceSelect.module.scss';
import { preventDefault } from '@/true-fsd/shared/utils/react';
import {
  accountPath,
  workspacePath,
  channelRequestPath,
  AdvertiserAccount,
  Workspace,
  ExistentAccountChannelRequest,
} from '@/true-fsd/data/advertiser-account';
import { PickNonNullable } from '@synapex/tools/typescript';

//style and behavior shared by all links
type SelectLinkProps = {
  className: string;
  children: NavLinkProps['children'];
  to: string;
};

const SelectLink = ({ className, to, children }: SelectLinkProps) => {
  const navLinkClassName: NavLinkProps['className'] = ({
    isActive,
    // isPending,
    // isTransitioning,
  }) => {
    return cl(classes.item, classes.link, className, {
      [classes.active]: isActive,
    });
    // return cl({
    //     [classes.active]: isActive,
    //     [classes.pending]: isPending,
    //     [classes.transitioning]: isTransitioning,
    // })
  };

  return (
    <NavLink to={to} className={navLinkClassName}>
      {children}
    </NavLink>
  );
};

type RenderWorkspaceProps = {
  workspace: Workspace | ExistentAccountChannelRequest;
};
const RenderWorkspace = ({ workspace }: RenderWorkspaceProps) => {
  const { text, imgSrc, Icon } =
    'workspace_type' in workspace
      ? workspaceMethods.getByType(workspace, {
          site: ({ favicon_url, hostname, pathname }) => {
            return {
              imgSrc: favicon_url,
              text: siteMethods.getTitleString({ hostname, pathname }),
              Icon: SiteIconSvg,
            };
          },
          channel: ({ channel_name, small_photo_data_url }) => {
            return {
              imgSrc: small_photo_data_url,
              text: channel_name,
              Icon: ChannelIconSvg,
            };
          },
        })
      : (() => {
          const { channel_name, small_photo_data_url } = workspace;
          return {
            imgSrc: small_photo_data_url,
            text: channel_name,
            Icon: ChannelIconSvg,
          };
        })();

  return (
    <>
      {imgSrc ? (
        <img src={imgSrc} className={classes.image} />
      ) : (
        <CirclePlaceholder className={cl(classes.image, classes.placeholder)} />
      )}
      <Icon className={classes.icon} />
      <div className={classes.text}>{text}</div>
      <div className={classes.spaceHolder} />
      <CheckIcon className={classes.check} />
    </>
  );
};

export type WorkspaceSelectProps = {
  className?: string;
  activeAccount: AdvertiserAccount | null;
  activeWorkspace: Workspace | ExistentAccountChannelRequest;
  workspaces: Workspace[];
  channelRequests: ExistentAccountChannelRequest[];
} & PickNonNullable<DropdownMenuProps, 'onOpenChange' | 'open'>;
export const WorkspaceSelect = ({
  className,
  activeAccount,
  activeWorkspace,
  workspaces,
  channelRequests,
  open,
  onOpenChange,
}: WorkspaceSelectProps) => {
  const newWorkspacePath = activeAccount
    ? 'workspace_type' in activeWorkspace
      ? workspacePath(activeWorkspace, 'new-workspace')
      : accountPath(activeAccount, 'workspace/new')
    : null;

  return (
    <Root modal open={open} onOpenChange={onOpenChange}>
      <Trigger
        className={cl(
          classes.item,
          classes.trigger,
          classes.workspace,
          className,
        )}
      >
        <RenderWorkspace workspace={activeWorkspace} />
        <ChevronDown className={classes.chevron} />
      </Trigger>
      <Portal>
        <Content
          onCloseAutoFocus={preventDefault}
          side={'bottom'}
          align="start"
          className={classes.content}
        >
          {newWorkspacePath && (
            <SelectLink to={newWorkspacePath} className={classes.button}>
              <div className={classes.text}>Добавить сайт или канал</div>
              <div className={classes.spaceHolder} />
              <PlusIcon className={classes.icon} />
            </SelectLink>
          )}

          {workspaces.map((ws) => {
            const path = workspacePath(ws);
            return (
              <SelectLink key={path} to={path} className={classes.workspace}>
                <RenderWorkspace workspace={ws} />
              </SelectLink>
            );
          })}
          {channelRequests.map((cr) => {
            const path = channelRequestPath(cr);

            return (
              <SelectLink key={path} to={path} className={classes.workspace}>
                <RenderWorkspace workspace={cr} />
              </SelectLink>
            );
          })}
        </Content>
      </Portal>
    </Root>
  );
};
